import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { HiX, HiMail, HiPlus, HiExclamationCircle } from 'react-icons/hi';
import { UserRole } from '../../types/manageUsers';

interface ModernEmailInputProps {
  emails: string[];
  onChange: (emails: string[]) => void;
  existingEmails?: Set<string>;
  pendingEmails?: Set<string>;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  showRoles?: boolean;
  onRoleChange?: (email: string, role: UserRole) => void;
  emailRoles?: Record<string, UserRole>;
}

const COMMON_DOMAINS = [
  '@gmail.com', '@yahoo.com', '@outlook.com', '@hotmail.com', '@icloud.com',
  '@aol.com', '@protonmail.com', '@live.com', '@msn.com', '@ymail.com'
];

const ModernEmailInput: React.FC<ModernEmailInputProps> = ({
  emails = [],
  onChange,
  existingEmails = new Set(),
  pendingEmails = new Set(),
  placeholder = "Enter email address...",
  className = "",
  disabled = false,
  showRoles = false,
  onRoleChange,
  emailRoles,
}) => {
  const [inputValue, setInputValue] = useState('');
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isFocused, setIsFocused] = useState(false);
  
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Memoized email sets for performance
  const emailSet = useMemo(() => new Set(emails.map(e => e.toLowerCase())), [emails]);

  // Generate suggestions efficiently
  const generateSuggestions = useCallback((value: string): string[] => {
    if (!value.trim()) return [];

    if (!value.includes('@')) {
      return COMMON_DOMAINS.slice(0, 5).map(domain => value + domain);
    }

    const [localPart, domainPart = ''] = value.split('@');
    
    // If user just typed @ (domainPart is empty), show all suggestions
    if (domainPart === '') {
      return COMMON_DOMAINS.slice(0, 5).map(domain => localPart + domain);
    }

    // Filter domains that start with the entered domain part
    return COMMON_DOMAINS
      .filter(domain => domain.toLowerCase().startsWith('@' + domainPart.toLowerCase()))
      .slice(0, 5)
      .map(domain => localPart + domain);
  }, []);

  // Validate and categorize email
  const getEmailStatus = useCallback((email: string) => {
    const lowerEmail = email.toLowerCase();
    
    if (emailSet.has(lowerEmail)) return { valid: false, type: 'duplicate' };
    if (existingEmails.has(lowerEmail)) return { valid: false, type: 'existing' };
    if (pendingEmails.has(lowerEmail)) return { valid: true, type: 'pending' };
    return { valid: true, type: 'valid' };
  }, [emailSet, existingEmails, pendingEmails]);

  // Add email with validation
  const addEmail = useCallback((email: string) => {
    const trimmedEmail = email.trim();
    const status = getEmailStatus(trimmedEmail);
    
    if (status.valid) {
      onChange([...emails, trimmedEmail.toLowerCase()]);
      setInputValue('');
      setSuggestions([]);
      setShowSuggestions(false);
      setSelectedIndex(0);
    }
  }, [emails, onChange, getEmailStatus]);

  // Remove email
  const removeEmail = useCallback((index: number) => {
    onChange(emails.filter((_, i) => i !== index));
  }, [emails, onChange]);

  // Clear all emails
  const clearAllEmails = useCallback(() => {
    onChange([]);
  }, [onChange]);

  // Handle input changes
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);
    
    const newSuggestions = generateSuggestions(value);
    setSuggestions(newSuggestions);
    setShowSuggestions(newSuggestions.length > 0 && value.length > 0);
    setSelectedIndex(0);
  }, [generateSuggestions]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (showSuggestions && suggestions.length > 0) {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex(prev => (prev + 1) % suggestions.length);
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex(prev => prev === 0 ? suggestions.length - 1 : prev - 1);
          break;
        case 'Tab':
          e.preventDefault();
          setInputValue(suggestions[selectedIndex]);
          setShowSuggestions(false);
          break;
        case 'Enter':
          e.preventDefault();
          addEmail(suggestions[selectedIndex]);
          break;
        case 'Escape':
          setShowSuggestions(false);
          break;
      }
    } else {
      switch (e.key) {
        case 'Enter':
        case ',':
        case ';':
          e.preventDefault();
          if (inputValue.trim()) addEmail(inputValue);
          break;
        case 'Tab':
          if (inputValue.trim()) {
            e.preventDefault();
            addEmail(inputValue);
          }
          break;
      }
    }
  }, [showSuggestions, suggestions, selectedIndex, inputValue, addEmail]);

  // Handle suggestion click - Fixed to properly close suggestions
  const handleSuggestionClick = useCallback((suggestion: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    addEmail(suggestion);
  }, [addEmail]);

  // Handle focus events
  const handleFocus = useCallback(() => {
    setIsFocused(true);
    if (inputValue) {
      const newSuggestions = generateSuggestions(inputValue);
      setSuggestions(newSuggestions);
      setShowSuggestions(newSuggestions.length > 0);
      setSelectedIndex(0);
    }
  }, [inputValue, generateSuggestions]);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
        setIsFocused(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Get validation message for current input
  const currentInputStatus = useMemo(() => {
    if (!inputValue.trim()) return null;
    return getEmailStatus(inputValue);
  }, [inputValue, getEmailStatus]);

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Compact Input Field */}
      <div className="relative" ref={containerRef}>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <HiMail className="h-5 w-5 text-gray-400" />
          </div>
          
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={handleFocus}
            onBlur={() => setIsFocused(false)}
            placeholder={placeholder}
            disabled={disabled}
            className={`
              block w-full pl-10 pr-12 py-3 border rounded-xl text-sm transition-all duration-200
              ${isFocused 
                ? 'border-blue-500 ring-2 ring-blue-500/20 dark:border-blue-400 dark:ring-blue-400/20' 
                : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
              }
              ${currentInputStatus && !currentInputStatus.valid ? 'border-red-500 ring-red-500/20' : ''}
              bg-white dark:bg-gray-700 text-gray-900 dark:text-white
              placeholder-gray-500 dark:placeholder-gray-400
              focus:outline-none
              ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
            `}
          />
          
          {inputValue && (
            <button
              type="button"
              onClick={() => inputValue.trim() && addEmail(inputValue)}
              className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-blue-500 transition-colors"
            >
              <HiPlus className="h-5 w-5" />
            </button>
          )}
        </div>

        {/* Input Validation Message */}
        {currentInputStatus && !currentInputStatus.valid && (
          <div className="mt-1 flex items-center gap-1 text-xs text-red-600 dark:text-red-400">
            <HiExclamationCircle className="w-3 h-3" />
            <span>
              {currentInputStatus.type === 'duplicate' && 'Email already added'}
              {currentInputStatus.type === 'existing' && 'User already in organization'}
            </span>
          </div>
        )}

        {/* Suggestions Dropdown */}
        {showSuggestions && suggestions.length > 0 && (
          <div className="absolute z-50 w-full mt-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-xl shadow-lg overflow-hidden backdrop-blur-sm">
            <div className="px-3 py-2 text-xs font-medium text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-600">
              Suggestions
            </div>
            {suggestions.map((suggestion, index) => {
              const status = getEmailStatus(suggestion);
              return (
                <button
                  key={suggestion}
                  type="button"
                  className={`
                    w-full px-4 py-3 text-left transition-all duration-150
                    ${index === selectedIndex ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400' : 'text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-700/50'}
                    ${index === suggestions.length - 1 ? '' : 'border-b border-gray-100 dark:border-gray-700'}
                    ${!status.valid ? 'opacity-50' : ''}
                  `}
                  onClick={(e) => handleSuggestionClick(suggestion, e)}
                  disabled={!status.valid}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <HiMail className="w-4 h-4 text-gray-400" />
                      <span className="text-sm">{suggestion}</span>
                    </div>
                    {!status.valid && (
                      <span className="text-xs text-red-500">
                        {status.type === 'duplicate' && 'Added'}
                        {status.type === 'existing' && 'Member'}
                      </span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Email List Display */}
      {emails.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium text-gray-900 dark:text-white">
              Added Emails ({emails.length})
            </h4>
            <button
              type="button"
              onClick={clearAllEmails}
              className="text-xs text-gray-500 hover:text-red-500 dark:text-gray-400 dark:hover:text-red-400 transition-colors"
            >
              Clear all
            </button>
          </div>
          
          <div className="max-h-48 overflow-y-auto space-y-2 pr-2 scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600">
            {emails.map((email, index) => (
              <div
                key={email}
                className="group flex items-center justify-between py-2 px-3  bg-blue-50  dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800 hover:shadow-sm transition-all duration-200"
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="flex-shrink-0  flex items-center justify-center">
                    <HiMail className="w-4 h-4 text-blue-600 dark:text-blue-300" />
                  </div>
                  <span className="text-sm font-medium text-gray-900 dark:text-white truncate">
                    {email}
                  </span>
                </div>
                <div>
                  </div>
                <div className="flex items-center gap-2">
                  {showRoles && onRoleChange && emailRoles && (
                    <select
                      value={emailRoles[email]}
                      onChange={(e) => onRoleChange(email, e.target.value as UserRole)}
                      className="text-xs px-2 py-1 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-blue-500 dark:focus:ring-blue-400 cursor-pointer transition-colors"
                    >
                      <option value={UserRole.BASIC_MEMBER}>Member</option>
                      <option value={UserRole.ADMIN}>Admin</option>
                    </select>
                  )}
                  <button
                    type="button"
                    onClick={() => removeEmail(index)}
                    disabled={disabled}
                    className="flex-shrink-0 p-1.5 text-gray-400 hover:text-red-500 dark:text-gray-500 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-all duration-200"
                    title="Remove email"
                  >
                    <HiX className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Helper Text */}
      <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-4">
        <span>Press <kbd className="px-1 py-0.5 bg-gray-200 dark:bg-gray-700 rounded text-xs">Enter</kbd> to add, <kbd className="px-1 py-0.5 bg-gray-200 dark:bg-gray-700 rounded text-xs">Tab</kbd> to auto-fill</span>
      </div>
    </div>
  );
};

export default ModernEmailInput;